/**
 * Public Order Tracking Page
 * Allows anyone to track orders using Job ID or QR Code without authentication
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './PublicTracking.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const PublicTracking = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [trackingId, setTrackingId] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Extract tracking ID from URL on mount
  useEffect(() => {
    // Check for identifier in route params first
    if (identifier) {
      setTrackingId(identifier);
      handleTrackById(identifier);
    }
    // Check for hash fragment (e.g., #5163)
    else if (location.hash) {
      const hashValue = location.hash.substring(1); // Remove the # symbol
      setTrackingId(hashValue);
      handleTrackById(hashValue);
    }
  }, [identifier, location.hash]);

  const handleTrackById = async (id) => {
    if (!id || !id.trim()) {
      return;
    }

    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      // Clean the tracking ID - remove any leading # or /
      const cleanId = id.replace(/^[/#]+/, '');

      const response = await fetch(`${API_BASE_URL}/job/track/${encodeURIComponent(cleanId)}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Order not found');
      }

      setOrderData(data.data.job);
    } catch (err) {
      setError(err.message || 'Failed to track order. Please check your tracking ID.');
      console.error('Tracking error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) {
      setError('Please enter a Job ID or QR Code');
      return;
    }
    handleTrackById(trackingId);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#FFA500',
      'Assigned': '#3498DB',
      'In Transit': '#9B59B6',
      'Delivered': '#27AE60',
      'Exception': '#E74C3C',
      'Returned': '#95A5A6'
    };
    return colors[status] || '#666';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': '⏳',
      'Assigned': '👤',
      'In Transit': '🚚',
      'Delivered': '✅',
      'Exception': '⚠️',
      'Returned': '↩️'
    };
    return icons[status] || '📦';
  };

  return (
    <div className="public-tracking-page">
      {/* Header */}
      <header className="tracking-header">
        <div className="tracking-header-content">
          <h1 className="tracking-logo">Marquee Couriers</h1>
          <p className="tracking-tagline">Track Your Order</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="tracking-main">
        <div className="tracking-container">
          {/* Search Form */}
          <div className="tracking-search-card">
            <h2 className="tracking-search-title">Enter Tracking Information</h2>
            <form onSubmit={handleTrack} className="tracking-search-form">
              <div className="tracking-input-wrapper">
                <input
                  type="text"
                  className="tracking-input"
                  placeholder="Enter Job ID (e.g., #5161) or QR Code"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="submit"
                  className="tracking-submit-btn"
                  disabled={loading || !trackingId.trim()}
                >
                  {loading ? 'Tracking...' : 'Track Order'}
                </button>
              </div>
            </form>

            {error && (
              <div className="tracking-error">
                {error}
              </div>
            )}
          </div>

          {/* Order Details */}
          {orderData && (
            <div className="tracking-results">
              {/* Status Card */}
              <div className="tracking-status-card">
                <div className="tracking-status-header">
                  <span className="tracking-status-icon" style={{ fontSize: '48px' }}>
                    {getStatusIcon(orderData.status)}
                  </span>
                  <div className="tracking-status-info">
                    <h3 className="tracking-job-id">Order {orderData.jobId}</h3>
                    <div
                      className="tracking-status-badge"
                      style={{ backgroundColor: getStatusColor(orderData.status) }}
                    >
                      {orderData.status}
                    </div>
                  </div>
                </div>
              </div>

              {/* Route Information */}
              <div className="tracking-route-card">
                <h3 className="tracking-section-title">Delivery Route</h3>
                <div className="tracking-route-details">
                  <div className="tracking-location">
                    <div className="tracking-location-icon pickup">📍</div>
                    <div className="tracking-location-info">
                      <p className="tracking-location-label">Pickup Location</p>
                      <p className="tracking-location-name">{orderData.from.name}</p>
                      <p className="tracking-location-address">{orderData.from.address}</p>
                    </div>
                  </div>

                  <div className="tracking-route-line"></div>

                  <div className="tracking-location">
                    <div className="tracking-location-icon delivery">🎯</div>
                    <div className="tracking-location-info">
                      <p className="tracking-location-label">Delivery Location</p>
                      <p className="tracking-location-name">{orderData.to.name}</p>
                      <p className="tracking-location-address">{orderData.to.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver Information */}
              {orderData.driver && (
                <div className="tracking-driver-card">
                  <h3 className="tracking-section-title">Driver Information</h3>
                  <div className="tracking-driver-info">
                    <div className="tracking-driver-avatar">
                      {orderData.driver.firstName.charAt(0)}{orderData.driver.lastName.charAt(0)}
                    </div>
                    <div>
                      <p className="tracking-driver-name">
                        {orderData.driver.firstName} {orderData.driver.lastName}
                      </p>
                      {orderData.driver.vehicle && (
                        <p className="tracking-driver-vehicle">
                          {orderData.driver.vehicle.make} {orderData.driver.vehicle.model} - {orderData.driver.vehicle.licensePlate}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="tracking-timeline-card">
                <h3 className="tracking-section-title">Order Timeline</h3>
                <div className="tracking-timeline">
                  {orderData.statusHistory && orderData.statusHistory.length > 0 ? (
                    orderData.statusHistory
                      .slice()
                      .reverse()
                      .map((history, index) => (
                        <div key={index} className="tracking-timeline-item">
                          <div className="tracking-timeline-marker" style={{ backgroundColor: getStatusColor(history.status) }}>
                            <span>{getStatusIcon(history.status)}</span>
                          </div>
                          <div className="tracking-timeline-content">
                            <p className="tracking-timeline-status">{history.status}</p>
                            <p className="tracking-timeline-time">
                              {new Date(history.timestamp).toLocaleString()}
                            </p>
                            {history.notes && (
                              <p className="tracking-timeline-notes">{history.notes}</p>
                            )}
                          </div>
                        </div>
                      ))
                  ) : (
                    <p className="tracking-no-history">No status updates available</p>
                  )}
                </div>
              </div>

              {/* Delivery Information */}
              {orderData.deliveredAt && (
                <div className="tracking-delivery-card">
                  <h3 className="tracking-section-title">Delivery Confirmation</h3>
                  <p className="tracking-delivery-time">
                    Delivered on {new Date(orderData.deliveredAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="tracking-footer">
        <p>&copy; 2024 Marquee Couriers. All rights reserved.</p>
        <button
          className="tracking-footer-link"
          onClick={() => navigate('/login')}
        >
          Client Login
        </button>
      </footer>
    </div>
  );
};

export default PublicTracking;
